import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getAuth, User } from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  getDocs,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { AppDispatch } from "./store";

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */

export interface PartnerAddress {
  street?:    string;
  city?:      string;
  country?:   string;
  zip?:       string;
  latitude?:  number | null;
  longitude?: number | null;
}

export interface DeliveryZone {
  type?:         "local" | "regional" | "international";
  country:       string;
  city:          string;
  neighborhood:  string;
  region:        string;
  zipCode:       string;
  latitude:      number | null;
  longitude:     number | null;
  deliveryFee:   number;
  deliveryTime:  number;
  fulfilledBy: {
    isFBA: boolean;
    isFBS: boolean;
  };
}

export interface CommercePartner {
  brandId:       string;
  userId:        string;

  name?:          string;
  categoryId?:    number | null;
  subcategoryId?: number | null;
  businessType?:  number | null;
  category?:      string | null;

  address?: {
    country?:      string;
    street?:       string;
    neighborhood?: string;
    city?:         string;
    county?:       string;
    latitude?:     number;
    longitude?:    number;
  };

  phone?: string | null;
  image?: string | null;

  joinDate: string;

  isPublished: boolean;
  isFlagged:   boolean;
  isComplete:  boolean;
  inReview:    boolean;
  isDraft:     boolean;

  deleted?: boolean;

  [key: string]: any;
}

interface CommercePartnersState {
  partners:         CommercePartner[];
  currentPartner:   CommercePartner | null;
  countryPartners:  CommercePartner[];   // ← from listenToMerchantsByCountry
  loading:          boolean;
  error:            string | null;
}

const initialState: CommercePartnersState = {
  partners:        [],
  currentPartner:  null,
  countryPartners: [],
  loading:         false,
  error:           null,
};

/* ─────────────────────────────────────────────
   FIREBASE
───────────────────────────────────────────── */

const db      = getFirestore();
const auth    = getAuth();
const storage = getStorage();

/* ─────────────────────────────────────────────
   PARSER
───────────────────────────────────────────── */

const parsePartner = (id: string, raw: Record<string, any>): CommercePartner => {
  const a = raw.address || {};
  return {
    brandId:      id,
    userId:       raw.userId ?? "",
    name:         raw.name ?? "",
    categoryId:   raw.categoryId ?? null,
    subcategoryId:raw.subcategoryId ?? null,
    businessType: raw.businessType ?? null,
    category:     raw.category ?? "",
    address: {
      street:       a.street       ?? "",
      city:         a.city         ?? "",
      country:      a.country      ?? "",
      neighborhood: a.neighborhood ?? "",
      county:       a.county       ?? "",
      latitude:  typeof a.latitude  === "string" ? Number(a.latitude)  : a.latitude  ?? null,
      longitude: typeof a.longitude === "string" ? Number(a.longitude) : a.longitude ?? null,
    },
    phone:    raw.phone    ?? "",
    image:    raw.image    ?? "",
    joinDate: raw.joinDate ?? new Date().toISOString(),
    isPublished: raw.isPublished ?? false,
    isFlagged:   raw.isFlagged   ?? false,
    isComplete:  raw.isComplete  ?? false,
    inReview:    raw.inReview    ?? false,
    isDraft:     raw.isDraft     ?? true,
    deleted:     raw.deleted     ?? false,
    delivery: Array.isArray(raw.delivery)
      ? raw.delivery.map((z: any) => ({
          type:         z.type         ?? "local",
          country:      z.country      ?? "",
          city:         z.city         ?? "",
          neighborhood: z.neighborhood ?? "",
          region:       z.region       ?? "",
          zipCode:      z.zipCode      ?? "",
          latitude:     z.latitude     ?? null,
          longitude:    z.longitude    ?? null,
          deliveryFee:  z.deliveryFee  ?? 0,
          deliveryTime: z.deliveryTime ?? 1,
          fulfilledBy: {
            isFBA: z.fulfilledBy?.isFBA ?? false,
            isFBS: z.fulfilledBy?.isFBS ?? true,
          },
        }))
      : [],
    fulfilledBy: {
      isFBA: raw.fulfilledBy?.isFBA ?? false,
      isFBS: raw.fulfilledBy?.isFBS ?? true,
    },
  };
};

/* ─────────────────────────────────────────────
   DISTANCE HELPER
───────────────────────────────────────────── */

const getDistanceKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R    = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/* ─────────────────────────────────────────────
   NEAREST DELIVERY
───────────────────────────────────────────── */

export const nearestDeliveryLocation = createAsyncThunk<
  { brandId: string; deliveryFee: number; matchedZone: any | null },
  { brandId: string; userLatitude: number; userLongitude: number }
>(
  "commercePartners/deliveryLocation",
  async ({ brandId, userLatitude, userLongitude }) => {
    const snap = await getDoc(doc(db, "commercePartners", brandId));
    if (!snap.exists()) return { brandId, deliveryFee: 0, matchedZone: null };

    const partner = snap.data();
    const zones   = partner.delivery ?? [];
    let matchedZone: any   = null;
    let shortestDistance   = Infinity;

    for (const zone of zones) {
      if (zone.type !== "local") continue;
      if (zone.latitude == null || zone.longitude == null) continue;
      const distance = getDistanceKm(userLatitude, userLongitude, zone.latitude, zone.longitude);
      if (distance <= 50 && distance < shortestDistance) {
        shortestDistance = distance;
        matchedZone = zone;
      }
    }
    if (!matchedZone) {
      matchedZone = zones.find(
        (z: any) => z.type === "regional" && z.country === partner.address?.country
      ) ?? null;
    }
    if (!matchedZone) {
      matchedZone = zones.find((z: any) => z.type === "international") ?? null;
    }
    return { brandId, deliveryFee: matchedZone?.deliveryFee ?? 0, matchedZone };
  }
);

/* ─────────────────────────────────────────────
   UPSERT PARTNER
───────────────────────────────────────────── */

export const upsertCommercePartner = createAsyncThunk<
  CommercePartner,
  { brandId?: string; data: Partial<CommercePartner> }
>("commercePartners/upsert", async ({ brandId, data }) => {
  const user = auth.currentUser as User | null;
  if (!user) throw new Error("User not authenticated");

  const colRef = collection(db, "commercePartners");

  const clean = (obj: any) => {
    const out: any = {};
    Object.keys(obj).forEach((k) => { if (obj[k] !== undefined) out[k] = obj[k]; });
    return out;
  };

  const normalized = clean({
    ...data,
    address: data.address
      ? clean({
          ...data.address,
          latitude:  typeof data.address.latitude  === "string" ? Number(data.address.latitude)  : data.address.latitude,
          longitude: typeof data.address.longitude === "string" ? Number(data.address.longitude) : data.address.longitude,
        })
      : undefined,
  });

  const updateBase = { ...normalized, updatedAt: serverTimestamp() };

  if (brandId) {
    const refDoc = doc(colRef, brandId);
    await updateDoc(refDoc, updateBase);
    const snap = await getDoc(refDoc);
    return parsePartner(snap.id, snap.data() || {});
  }

  const createBase = {
    ...updateBase,
    userId:      user.uid,
    joinDate:    new Date().toISOString(),
    isPublished: false,
    isFlagged:   false,
    isComplete:  false,
    inReview:    false,
    isDraft:     true,
    createdAt:   serverTimestamp(),
  };

  const newRef = await addDoc(colRef, createBase);
  await updateDoc(newRef, { brandId: newRef.id });
  const snap = await getDoc(newRef);
  return parsePartner(newRef.id, snap.data() || {});
});

/* ─────────────────────────────────────────────
   UPLOAD LOGO
───────────────────────────────────────────── */

export const uploadCommercePartnerLogo = createAsyncThunk<
  string,
  { brandId: string; file: File }
>("commercePartners/uploadLogo", async ({ brandId, file }, { dispatch }) => {
  const path    = `commercePartners/${brandId}/logo/${file.name}`;
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, file);
  const url = await getDownloadURL(fileRef);
  await dispatch(upsertCommercePartner({ brandId, data: { image: url } }));
  return url;
});

/* ─────────────────────────────────────────────
   LISTENERS
───────────────────────────────────────────── */

let userPartnersUnsub: null | (() => void) = null;

export const listenToUserCommercePartners =
  (userId: string) => (dispatch: AppDispatch) => {
    if (userPartnersUnsub) userPartnersUnsub();
    const q = query(collection(db, "commercePartners"), where("userId", "==", userId));
    userPartnersUnsub = onSnapshot(q, (snap) => {
      dispatch(setPartners(snap.docs.map((d) => parsePartner(d.id, d.data()))));
    });
  };

export const listenToCommerceData =
  (brandId: string) => (dispatch: AppDispatch) => {
    const docRef = doc(db, "commercePartners", brandId);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          dispatch(commercePartnersSlice.actions.setCurrentPartner(
            parsePartner(docSnap.id, docSnap.data() || {})
          ));
        } else {
          dispatch(commercePartnersSlice.actions.setCurrentPartner(null));
        }
      },
      (error) => {
        dispatch(commercePartnersSlice.actions.setError(error.message));
      }
    );
    return unsubscribe;
  };

/* ─────────────────────────────────────────────
   FETCH ALL
───────────────────────────────────────────── */

export const fetchAllCommercePartners = createAsyncThunk<CommercePartner[]>(
  "commercePartners/fetchAll",
  async () => {
    const snap = await getDocs(collection(db, "commercePartners"));
    return snap.docs.map((d) => parsePartner(d.id, d.data()));
  }
);

/* ─────────────────────────────────────────────
   LISTEN TO MERCHANTS BY COUNTRY (real-time)
   Writes to state.countryPartners — only published
───────────────────────────────────────────── */

let countryPartnersUnsub: null | (() => void) = null;

export const listenToMerchantsByCountry =
  (country: string) => (dispatch: AppDispatch) => {
    // clean up previous listener
    if (countryPartnersUnsub) {
      countryPartnersUnsub();
      countryPartnersUnsub = null;
    }

    if (!country) {
      dispatch(commercePartnersSlice.actions.setCountryPartners([]));
      return;
    }

    const q = query(
      collection(db, "commercePartners"),
      where("address.country", "==", country),
      where("isPublished",     "==", true)
    );

    countryPartnersUnsub = onSnapshot(
      q,
      (snap) => {
        dispatch(
          commercePartnersSlice.actions.setCountryPartners(
            snap.docs.map((d) => parsePartner(d.id, d.data()))
          )
        );
      },
      (err) => {
        dispatch(commercePartnersSlice.actions.setError(err.message));
      }
    );

    return countryPartnersUnsub;
  };

export const stopMerchantsByCountryListener = () => {
  if (countryPartnersUnsub) {
    countryPartnersUnsub();
    countryPartnersUnsub = null;
  }
};

/* ─────────────────────────────────────────────
   SLICE
───────────────────────────────────────────── */

const commercePartnersSlice = createSlice({
  name: "commercePartners",
  initialState,
  reducers: {
    setPartners: (state, a: PayloadAction<CommercePartner[]>) => {
      state.partners = a.payload;
    },
    setCurrentPartner: (state, a: PayloadAction<CommercePartner | null>) => {
      state.currentPartner = a.payload;
    },
    setCountryPartners: (state, a: PayloadAction<CommercePartner[]>) => {
      state.countryPartners = a.payload;
    },
    clearCommercePartners: (state) => {
      state.partners       = [];
      state.currentPartner = null;
      state.countryPartners = [];
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (b) => {
    b.addCase(upsertCommercePartner.fulfilled, (state, a) => {
      const i = state.partners.findIndex((p) => p.brandId === a.payload.brandId);
      if (i !== -1) state.partners[i] = a.payload;
      else state.partners.push(a.payload);
      state.currentPartner = a.payload;
      state.loading = false;
    });

    b.addCase(fetchAllCommercePartners.pending,   (state) => { state.loading = true; state.error = null; });
    b.addCase(fetchAllCommercePartners.fulfilled, (state, a) => { state.partners = a.payload; state.loading = false; });
    b.addCase(fetchAllCommercePartners.rejected,  (state, a: any) => { state.loading = false; state.error = a.error?.message || "Failed"; });

    b.addMatcher(
      (a) => a.type.startsWith("commercePartners/") && a.type.endsWith("/pending"),
      (state) => { state.loading = true; state.error = null; }
    );
    b.addMatcher(
      (a) => a.type.startsWith("commercePartners/") && a.type.endsWith("/rejected"),
      (state, a: any) => { state.loading = false; state.error = a.payload || "Something went wrong"; }
    );
  },
});

export const {
  setPartners,
  setCurrentPartner,
  setCountryPartners,
  clearCommercePartners,
} = commercePartnersSlice.actions;

export default commercePartnersSlice.reducer;